/**
 * Stage footer configuration — the config contract for the per-stage
 * calculation footer on the pipeline board.
 *
 * Mirrors the null-vs-empty resolution logic of the card config
 * (`cardConfigResolution.ts`) with ONE deliberate difference: an explicit
 * empty `calculations: []` is a valid "no footer" state and is returned as-is
 * (it does NOT fall back to the default). Only a null / malformed / missing
 * `calculations` array falls back to the default.
 *
 * The config is persisted on the pipeline CRM entity in the
 * `ahoy_stage_footer_config` JSON property.
 */

import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

// ---------------------------------------------------------------------------
// Config types
// ---------------------------------------------------------------------------

export type StageFooterFunction = 'sum' | 'avg' | 'count';

export interface StageFooterCalculation {
  function: StageFooterFunction;
  /** Required for `sum`/`avg`; omitted for `count`. */
  property?: string;
}

export interface PipelineFooterConfig {
  calculations: StageFooterCalculation[];
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Default footer config.
 *
 * Deal pipelines (underlying object type `ahoy_deal`) default to summing both
 * the deal amount and the weighted amount. Every other pipeline type defaults
 * to NO footer.
 */
export function buildDefaultFooterConfig(
  isDealPipeline: boolean
): PipelineFooterConfig {
  if (isDealPipeline) {
    return {
      calculations: [
        { function: 'sum', property: 'ahoy_amount' },
        { function: 'sum', property: 'ahoy_weighted_amount' },
      ],
    };
  }
  return { calculations: [] };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Drop any calculation whose property no longer exists in the schema, or whose
 * function isn't an allowed aggregation for that property's field type. `count`
 * is always valid and needs no property. Order is preserved.
 */
export function validateFooterConfig(
  config: PipelineFooterConfig,
  propertySchemas: Record<string, PropertyTypeSchema>,
  fieldTypeAggregations: Record<string, string[]>
): PipelineFooterConfig {
  const calculations = config.calculations.filter(calc => {
    if (calc.function === 'count') {
      return true;
    }
    if (!calc.property) {
      return false;
    }
    const schema = propertySchemas[calc.property];
    if (!schema) {
      return false;
    }
    const allowed = fieldTypeAggregations[schema.field_type_name] ?? [];
    return allowed.includes(calc.function);
  });
  return { calculations };
}

// ---------------------------------------------------------------------------
// Effective-config resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the effective footer config from a raw persisted value.
 *
 * - `raw` null / undefined / not-an-object / missing `calculations` array →
 *   the default (deal-only).
 * - An explicit `{ calculations: [...] }` → used verbatim after validation.
 *   An explicit empty `calculations: []` is a deliberate "no footer" state and
 *   is returned as-is — it does NOT fall back to the default.
 */
export function resolveEffectiveFooterConfig(
  raw: unknown,
  isDealPipeline: boolean,
  propertySchemas: Record<string, PropertyTypeSchema>,
  fieldTypeAggregations: Record<string, string[]>
): PipelineFooterConfig {
  if (
    raw == null ||
    typeof raw !== 'object' ||
    !Array.isArray((raw as PipelineFooterConfig).calculations)
  ) {
    return buildDefaultFooterConfig(isDealPipeline);
  }

  const parsed = raw as PipelineFooterConfig;
  return validateFooterConfig(parsed, propertySchemas, fieldTypeAggregations);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Distinct property names referenced by the config's calculations. */
export function extractFooterPropertyNames(
  config: PipelineFooterConfig
): string[] {
  const names = new Set<string>();
  for (const calc of config.calculations) {
    if (calc.property) {
      names.add(calc.property);
    }
  }
  return [...names];
}
